import Backbone, {AddOptions} from 'backbone';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import * as SequenceAnalysisConstants from 'constants/SequenceAnalysisConstants';
import {FilePreferences} from 'folder-items/FileConstants';
import {getTabParam} from 'lib/urlTabParam';
import preferences from 'Preferences';
import * as logging from 'util/Logging';
import uniqueId from 'util/uniqueId';

export const PREFERENCE_KEY_IGNORE = 'PREFERENCE_KEY_IGNORE';

export interface TabStateDefaults {
  title: string;
  scroll: number;
  active: boolean;
  closeable: boolean;
  attached: boolean;
  id: string;
  options: boolean;
  preferenceKey: string | null;
  analysis: unknown | null;
  showInNavBar: boolean;
  draggable: boolean;
  greyTab: boolean;
  inBeta: boolean;
}

/**
 * A Model backing a single buffer tab state whose Backbone View is associated with the model in BufferUtil.
 */
export class TabState extends Backbone.Model {
  cleanUp?(): void;
  saveScrollState?(): void;

  override defaults(): TabStateDefaults {
    return {
      title: 'New Tab',
      scroll: 0,
      active: false,
      closeable: true,
      attached: false,
      id: uniqueId(),
      options: true,
      preferenceKey: null,
      analysis: null,
      showInNavBar: true,
      draggable: true,
      greyTab: false, // render grey background in the tab and the tabview (even when active)
      // should also contain type and state fields to be able to build view off of
      // Render a badge indicating that the tab is in beta availability.
      inBeta: false,
    };
  }

  showInNavBar(): boolean {
    return this.get('showInNavBar');
  }

  title(): string {
    return this.get('title');
  }

  close(): void {
    if (this.cleanUp) {
      this.cleanUp();
    }
    if (this.collection == null) {
      return;
    }
    const tabs = this.collection;
    tabs.remove(this);
  }

  /**
   * Callback to execute when a tab is clicked on.
   *
   * Currently, just emits an analytics event.
   */
  onClick(): void {
    analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.ACTIVATED_TAB, {
      type: this.constructor.name,
    });
  }
}

/**
 * A Backbone Collection of TabState models.
 */
export class TabStates extends Backbone.Collection<TabState> {
  _active?: TabState | null;

  static initClass(): void {
    this.prototype.model = TabState;
  }

  updateActive(model: TabState): void {
    if (this._active === model) {
      return;
    }
    if (this._active) {
      this._active.set('active', false);
    }
    this._active = model;
    // Note: URL tab param is updated via FileState.syncTitleAndURL, not here.
    // This avoids race conditions when switching workspace items.
  }

  override initialize(models: Array<TabState>, options?: {}): void {
    super.initialize(models, options);
    this._active = null;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.on('change:active', this.updateActive);
    this.on('remove', (model: TabState, collection: Array<TabState>, {index = 0} = {}) => {
      this.handleTabRemovals(Math.max(index - 1, 0));
    });
    this.on('reset', () => {
      this.handleTabRemovals(0);
    });
    this.on('close', (tab: TabState) => {
      this.remove(tab);
    });
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.on('add remove', this.updateDigest);
  }

  /**
   * Override Backbone.Collection::add to insert a tab, by default, to the right of the currently active
   * tab if possible, and otherwise, fallback to the typical behavior of inserting at the end / at a
   * caller-specified 'at' index.
   */
  // @ts-expect-error - Backbone.Collection::add has overloaded type
  add(model: TabState, options?: AddOptions): TabState {
    options = options || {}; // Backbone supports null, so we do too.
    if (options.at == null) {
      const activeIndex = this.models.findIndex((tab) => tab.get('active'));
      options = {...options, at: activeIndex >= 0 ? activeIndex + 1 : undefined};
    }
    return super.add(model, options);
  }

  /**
   * Updates the internal tracker for the active tab, and activate the tab at the specified index if the
   * there is no active tab.
   *
   * @param {number} fallbackIndex - index of tab to activate if there is no active tab
   */
  handleTabRemovals(fallbackIndex: number): void {
    if (this._active && !this.contains(this._active)) {
      this._active = null;
    }
    if (this.size() && this.find((tab) => tab.get('active')) == null) {
      const tab = this.at(fallbackIndex) || this.at(0);
      tab.set('active', true);
    }
  }

  updateDigest(model: TabState): void {
    const analysis = model.get('analysis');
    if (analysis && analysis.get && analysis.get('analysis_type') === SequenceAnalysisConstants.DIGEST) {
      // This will impact if the Virtual Digest gets added or removed. To avoid interfering with our
      // active tab / insertion logic, we defer the handling.
      _.defer(() => {
        this.trigger('change:digest');
      });
    }
  }
}

TabStates.initClass();

interface BufferStateDefaults {
  tabs: TabStates;
  active: boolean;
  shareTabsWithOtherBuffers: boolean;
}

/**
 * A Backbone Model representing a collection of tabs that are part of a workspace item.
 *
 * Models one "pane", so in splitscreen mode a BufferState instance would represent one half
 * of the splitscreen.
 */
export class BufferState extends Backbone.Model {
  override defaults(): BufferStateDefaults {
    return {
      tabs: new TabStates(),
      active: true,
      shareTabsWithOtherBuffers: true,
    };
  }

  override initialize(attributes?: {}, options?: {}): void {
    super.initialize(attributes, options);
    // oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleChangeDigest = () => this.trigger('change:digest');
    this.listenTo(this.tabs(), 'change:digest', handleChangeDigest);
  }

  add(tab: TabState | Array<TabState>, options?: object): void {
    return this.get('tabs').add(tab, options);
  }

  tabs(): TabStates {
    return this.get('tabs');
  }

  isRightmostBuffer(): boolean {
    const numActive = this.collection.where({active: true}).length;
    const index = this.collection.indexOf(this);
    return (numActive === 1 && index === 0) || index === 1;
  }
}

/**
 * A Backbone Collection of BufferState models which should always contain either 1 or 2 models.
 *
 * A BufferStates collection will typically be referenced by a FileState model and contains data
 * about the buffers including which tabs are open, their ordering, and which are active.
 *
 * When a workspace file is open and in splitscreen mode, there should be two BufferState models
 * modeling each side of the splitscreen. When not in splitscreen mode, the collection should
 * have a length of just one.
 */
export class BufferStates extends Backbone.Collection<BufferState> {
  static initClass(): void {
    this.prototype.model = BufferState;
  }

  getBuffersLength(): number {
    return this.where({active: true}).length;
  }

  getLayoutPreferenceKey(): string {
    // eslint-disable-next-line no-restricted-syntax
    const tabPreferenceKeys = this.chain()
      .map((buffer: BufferState) => buffer.tabs().models)
      .flatten()
      .filter((tab: TabState) => !tab.get('closeable'))
      .filter((tab: TabState) => {
        if (tab.get('preferenceKey') === PREFERENCE_KEY_IGNORE) {
          return false;
        } else if (!tab.get('preferenceKey')) {
          logging.warn(`Found non-closeable tab without defined preferenceKey: ${tab.get('title')}`);
          return false;
        }
        return true;
      })
      .map((tab: TabState) => tab.get('preferenceKey'))
      .sort()
      .value();

    // Key on split state (length of buffers indicates whether workspace is split) in addition to
    // sorted list of tab keys
    return `${this.getBuffersLength()}:${tabPreferenceKeys.join(',')}`;
  }

  addTab(tab: TabState, options?: {side?: 'left' | 'right'; active?: boolean}): void {
    let side = options?.side;
    if (side == null) {
      side = 'right';
    }
    let active = options?.active;
    if (active == null) {
      active = true;
    }

    let buffer: BufferState | null = null;
    if (side === 'left') {
      buffer = this.findWhere({active: true});
    } else {
      for (let index = this.length - 1; index >= 0; index--) {
        if (this.at(index).get('active')) {
          buffer = this.at(index);
          break;
        }
      }
    }
    buffer!.tabs().add(tab);
    tab.set({active});
    analytics.track('editor', 'open_tab', {title: tab.title()});
  }

  moveTab(tab: TabState, buffer: BufferState, index: number): void {
    const oldBuffer = this.find((buf) => buf.tabs().contains(tab));
    oldBuffer.tabs().remove(tab);
    tab.set({active: false});
    buffer.tabs().add(tab, {at: index});
    tab.set({active: true});
    oldBuffer.trigger('reorderTabs');
    if (oldBuffer !== buffer) {
      buffer.trigger('reorderTabs');
    }
  }

  getTabByClass(cls: typeof TabState): TabState | undefined {
    return this.getTab((tab) => tab instanceof cls);
  }

  getTab(predicate: (tab: TabState) => boolean): TabState {
    return this.chain()
      .map((buffer: BufferState) => buffer.tabs().models)
      .flatten()
      .find(predicate)
      .value();
  }

  loadLayout(fileType: string): void {
    const savedPreference = preferences.get(`${FilePreferences.WORK_SPACE_BUFFERS_NAMESPACE}${fileType}`);
    if (!savedPreference) {
      return;
    }
    const buffersPref = savedPreference[this.getLayoutPreferenceKey()];
    if (!buffersPref) {
      return;
    }

    if (this.getBuffersLength() === 1 && buffersPref[0].length === 0) {
      // This is unexpected -- if there is a saved preference, there should always be at least
      // one item in the first buffer in an unsplit workspace . However, in the event that
      // we get into an unexpected preference state where the single buffer is empty, defensively
      // do not load tab layout from the bad preference value.
      return;
    }

    // Move tabs to the correct buffers and set `active` based on preference
    buffersPref.forEach((bufferPref: Array<{id: number; active: boolean}>, bufferIndex: number) => {
      bufferPref.forEach((tabPref: {id: number; active: boolean}, tabIndex: number) => {
        const tabState = this.getTab((tab) => tab.get('preferenceKey') === tabPref.id);
        if (tabState) {
          const oldBuffer = this.find((buf) => buf.tabs().contains(tabState));
          const newBuffer = this.at(bufferIndex);
          oldBuffer.tabs().remove(tabState);
          tabState.set({active: false});
          newBuffer.tabs().add(tabState, {at: tabIndex});
          tabState.set({active: tabPref.active});
        }
      });
    });
  }

  /**
   * Activates a tab by its identifier (preferenceKey or id).
   * First searches by preferenceKey, then falls back to searching by id.
   * This is used to restore tab state from URL parameters.
   * @param tabIdentifier The preferenceKey or id of the tab to activate.
   * @returns true if a matching tab was found and activated, false otherwise.
   */
  activateTabByIdentifier(tabIdentifier: string): boolean {
    // First, try to find by preferenceKey
    let tab = this.getTab(
      (t) => t.get('preferenceKey') === tabIdentifier && t.get('preferenceKey') !== PREFERENCE_KEY_IGNORE
    );

    // If not found by preferenceKey, try to find by id
    if (tab == null) {
      tab = this.getTab((t) => t.get('id') === tabIdentifier);
    }

    if (tab != null) {
      tab.set({active: true});
      return true;
    }
    return false;
  }

  /**
   * @deprecated Use activateTabByIdentifier instead
   */
  activateTabByPreferenceKey(preferenceKey: string): boolean {
    return this.activateTabByIdentifier(preferenceKey);
  }

  /**
   * Activates a tab based on the URL tab parameter if present.
   * Should be called after tabs are initialized.
   * @returns true if a tab was activated from the URL, false otherwise.
   */
  activateTabFromUrl(): boolean {
    const tabParam = getTabParam();
    if (tabParam != null) {
      return this.activateTabByIdentifier(tabParam);
    }
    return false;
  }
}

BufferStates.initClass();
